import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { changeBotMessageToText, setNewMessage } from 'app/redux/messages/messages.actions'
import { getTimeByTimezone } from 'app/utils/date'
import RobotOptionsBox from 'app/components/RobotOptionsBox'
import { playNotifSound } from 'app/utils/sound'
import BotReturnButton from 'app/components/BotReturnButton'
import ThreeDotsLoader from 'app/components/ThreeDotsLoader'
import Text from '../components/Text'
import Emoji from '../components/Emoji'
import Voice from '../components/Voice'
import ImageMessage from '../components/Image'
import FileMessage from '../components/File'
import VideoMessage from '../components/Video'
import Reply from '../components/Reply'
import ReplyPreview from '../components/ReplyPreview'
import EditedBadge from '../components/EditedBadge'
import OperatorAvatarWithName from '../components/OperatorAvatarWithName'
import AutoMessage from '../components/AutoMessage'
import AiBotMsgWrapper from '../components/AiBotMsgWrapper'
import Time from '../components/Time'
import InnerMsgWrapper from '../components/InnerMsgWrapper'
import TextWrapper from '../components/TextWrapper'
import './operatorMessage.scss'
import clsx from 'clsx'

const getLanguageInfo = ({ widgetData }) => widgetData.widgetInfo.languageInfo
const getOperators = ({ widgetData }) => widgetData.widgetInfo.data.operators
const getDefaultOnlineAgent = ({ messagesData }) => messagesData.defaultOnlineAgent
const getActiveDepartment = ({ messagesData }) => messagesData.activeDepartment.data
const getAcceptedChat = ({ messagesData }) => messagesData.acceptedChat

const MESSAGE_TYPE_KEY = 'operator'

const OperatorMessage = ({
  withAvatar,
  isLastMsg,
  messageType,
  createdAt,
  messageContent,
  messageId,
  botId,
  messageReplyId,
  forcedFormUnfilled,
  sendAfterFormCompletion,
  isMessageEdited,
  messageTeamId,
  botMessageLevel,
  tmpId,
  isMsgAutoMsg,
  fileUrl,
  fileName,
  fileData,
  fileSize,
  conversationId,
  hideConnectWithOperatorBtn,
  isAiBotMsg,
  messageAuthor,
  autoMsgSender,
  aiBotName,
}) => {
  const displayBotStopeedDueToUnfilledForcedForm =
    messageType === 'bot_message' && forcedFormUnfilled && sendAfterFormCompletion

  const isResponsiveBotMsg = ['bot_text_response', 'bot_message'].includes(messageType)

  const [operatorLoadingMode, setOperatorLoadingMode] = useState(isResponsiveBotMsg)

  const showReplyIcon = !botId && !isMsgAutoMsg
  const activeDepartment = useSelector(getActiveDepartment)
  const languageInfo = useSelector(getLanguageInfo)
  const operators = useSelector(getOperators)
  const defaultOnlineAgent = useSelector(getDefaultOnlineAgent)
  const acceptedChat = useSelector(getAcceptedChat)
  const acceptedChatOperatorId = acceptedChat?.[activeDepartment]?.agentInfo?.id
  const operatorId = messageAuthor || autoMsgSender?.id || acceptedChatOperatorId || defaultOnlineAgent?.id
  const operator = operators.find((op) => op.id === operatorId)
  const operatorName = isAiBotMsg ? aiBotName || languageInfo.texts.chatBotText : operator ? operator.name : ''
  const dispatch = useDispatch()

  const language = languageInfo.general.language
  const returnButtonTexts = languageInfo.texts.botReturnButton
  const renderRepliedMsgPreview = messageReplyId && (
    <ReplyPreview msgRepliedOnId={messageReplyId} languageInfo={languageInfo} />
  )

  const timezone = language === 'en' ? 'en-GB' : 'fa-IR'
  const messageTime = createdAt && getTimeByTimezone(createdAt, timezone)

  const renderEditedMsgSubtitle = isMessageEdited && <EditedBadge languageInfo={languageInfo} />

  const handleRobotReturnButtonClick = () => {
    dispatch(
      setNewMessage({
        message: {
          type: 'bot_message',
          botId,
          botMessageLevel,
          me: false,
          tmpId: new Date().getTime(),
        },
        teamId: messageTeamId,
      }),
    )

    dispatch(changeBotMessageToText(tmpId, messageTeamId, messageContent))
  }

  const renderReturnButton = (
    <div>
      <BotReturnButton handleClick={handleRobotReturnButtonClick} text={returnButtonTexts} />
    </div>
  )

  const getProperMessage = () => {
    if (operatorLoadingMode) {
      return (
        <TextWrapper type={MESSAGE_TYPE_KEY} className="operator_msg_loader_wrapper">
          <ThreeDotsLoader />
        </TextWrapper>
      )
    }

    if (messageType === 'bot_message') {
      return <RobotOptionsBox msgBotLevel={botMessageLevel} botId={botId} teamId={messageTeamId} tmpId={tmpId} />
    }

    if (isMsgAutoMsg) {
      return <AutoMessage content={messageContent} />
    }

    switch (messageType) {
      case 'text':
      case 'bot_text_response':
        return (
          <div className="operator_and_bot_wrapper" title={messageTime}>
            <Text
              type={MESSAGE_TYPE_KEY}
              title={messageTime}
              textContent={messageContent}
              renderRepliedMsgPreview={renderRepliedMsgPreview}
              renderEditedMsgSubtitle={renderEditedMsgSubtitle}
              languageInfo={languageInfo}
            />
            {messageType === 'bot_text_response' && renderReturnButton}
          </div>
        )

      case 'emoji':
        return (
          <Emoji
            type={MESSAGE_TYPE_KEY}
            title={messageTime}
            renderRepliedMsgPreview={renderRepliedMsgPreview}
            renderEditedMsgSubtitle={renderEditedMsgSubtitle}
            content={messageContent}
          />
        )

      case 'audio':
        return <Voice type={MESSAGE_TYPE_KEY} url={fileUrl} renderRepliedMsgPreview={renderRepliedMsgPreview} />

      case 'image':
        return (
          <ImageMessage
            type={MESSAGE_TYPE_KEY}
            title={messageTime}
            renderRepliedMsgPreview={renderRepliedMsgPreview}
            url={fileUrl}
            alt={fileName}
          />
        )

      case 'compress':
      case 'file':
        return (
          <FileMessage
            type={MESSAGE_TYPE_KEY}
            title={messageTime}
            renderRepliedMsgPreview={renderRepliedMsgPreview}
            fileName={fileName}
            fileSize={fileSize}
            url={fileUrl}
          />
        )

      case 'video':
        return <VideoMessage url={fileData || fileUrl} />

      default:
        break
    }
  }

  useEffect(() => {
    if (operatorLoadingMode && !displayBotStopeedDueToUnfilledForcedForm) {
      playNotifSound()
      setTimeout(() => setOperatorLoadingMode(false), 1000)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayBotStopeedDueToUnfilledForcedForm, operatorLoadingMode])

  if (displayBotStopeedDueToUnfilledForcedForm) return <></>

  return (
    <>
      <div data-id={messageId} className="operator_text_message_container">
        <InnerMsgWrapper className={clsx(withAvatar && 'operator_with_avatar')} type={MESSAGE_TYPE_KEY}>
          {withAvatar && (
            <OperatorAvatarWithName
              operatorName={operatorName}
              operatorAvatar={operator?.avatar}
              isAiBotMsg={isAiBotMsg}
            />
          )}
          <div className="operator_time_and_content_wrapper">
            <div className="operator_msg_wrapper">
              <AiBotMsgWrapper
                isAiBotMsg={isAiBotMsg}
                conversationId={conversationId}
                hideConnectWithOperatorBtn={hideConnectWithOperatorBtn}
                activeDepartment={activeDepartment}
                isLastMsg={isLastMsg}
              >
                {getProperMessage()}
              </AiBotMsgWrapper>
            </div>

            <div className="operator_message_time_and_reply">
              <Time text={messageTime} />
              {showReplyIcon && (
                <Reply
                  type={MESSAGE_TYPE_KEY}
                  messageType={messageType}
                  messageContent={messageContent}
                  messageId={messageId}
                  languageInfo={languageInfo}
                />
              )}
            </div>
          </div>
        </InnerMsgWrapper>
      </div>
    </>
  )
}

export default OperatorMessage
